import { useState, useEffect, useCallback } from "react";
import { UpdateBoatDTO } from "../../../types/boat.types";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BoatsAPI } from "../../../APIs/boats.api";
import { useToast } from "../../../components/toast";
import BoatForm from "../../my-team/boat-information/boat-form";
import { Loading } from "../../../components/loading";
import { ErrorMessage } from "../../../components/error-message";

export function BoatEdit() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("teamId");

  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    registration_number: "",
    engine_brand: "",
    hp: 0,
    has_radio: false,
    is_yacht: false,
  });

  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();

  const handleCancel = useCallback(() => {
    if (teamId) {
      navigate(`/dashboard/teams/view/${teamId}`);
    } else {
      navigate(-1);
    }
  }, [teamId, navigate]);

  useEffect(() => {
    const loadBoatData = async () => {
      if (!id) return;

      try {
        const response = await BoatsAPI.getBoatById(parseInt(id));
        if (response.statusCode === 200) {
          const boat = response.data;
          setFormData({
            id: boat.id,
            name: boat.name,
            registration_number: boat.registration_number,
            engine_brand: boat.engine_brand,
            hp: boat.hp,
            has_radio: boat.has_radio,
            is_yacht: boat.is_yacht,
          });
        } else {
          addToast({
            message: "Error al cargar los datos de la embarcación",
            type: "error",
            location: "bottom-center",
          });
          handleCancel();
        }
      } catch (error) {
        addToast({
          message: "Error al cargar los datos de la embarcación",
          type: "error",
          location: "bottom-center",
        });
        handleCancel();
      } finally {
        setIsLoading(false);
      }
    };

    loadBoatData();
  }, [id, addToast, handleCancel]);

  function handleChange(e: React.ChangeEvent<HTMLElement>) {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? Number(value)
          : value,
    }));
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setIsSubmitting(true);
    try {
      const updateData: UpdateBoatDTO = {
        id: formData.id,
        name: formData.name,
        registration_number: formData.registration_number,
        engine_brand: formData.engine_brand,
        hp: formData.hp,
        has_radio: formData.has_radio,
        is_yacht: formData.is_yacht,
      };

      const response = await BoatsAPI.patchBoat(updateData);
      if (response.statusCode === 200) {
        addToast({
          message: "¡Embarcación actualizada!",
          type: "success",
          location: "bottom-center",
        });
        handleCancel();
      } else {
        addToast({
          message: response.message || "Error al actualizar la embarcación",
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al actualizar la embarcación, intente nuevamente",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!id) {
    return (
      <div className="min-h-screen mt-10">
        <ErrorMessage message="Error: ID de embarcación no encontrado" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen mt-10">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-10">
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
        <BoatForm
          createBoat={formData}
          title="Editar Embarcación"
          buttonText="Guardar"
          buttonVariant="save"
          isSubmitting={isSubmitting}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          handleChange={handleChange}
        />
      </div>
    </div>
  );
}
